import { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { cn } from "@/lib/utils";

export const EASE = [0.16, 1, 0.3, 1];

export const Reveal = ({ children, delay = 0, y = 28, className, ...rest }) => (
  <motion.div
    initial={{ opacity: 0, y }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-80px" }}
    transition={{ duration: 0.9, ease: EASE, delay }}
    className={className}
    {...rest}
  >
    {children}
  </motion.div>
);

export const MaskLines = ({ lines, className, lineClass, delay = 0, stagger = 0.12, onView = false }) => {
  const trigger = onView ? { whileInView: "show", viewport: { once: true, margin: "-60px" } } : { animate: "show" };
  const line = {
    hidden: { y: "110%" },
    show: (i) => ({ y: "0%", transition: { duration: 1.1, ease: EASE, delay: delay + i * stagger } }),
  };
  return (
    <motion.span className={cn("block", className)} initial="hidden" {...trigger}>
      {lines.map((l, i) => (
        <span key={i} className="block overflow-hidden pb-[0.08em]">
          <motion.span className={cn("block", lineClass?.(i))} variants={line} custom={i}>
            {l}
          </motion.span>
        </span>
      ))}
    </motion.span>
  );
};

const GLYPHS = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789#%&*+=";

export const Scramble = ({ text, className }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-40px" });
  const [out, setOut] = useState(text);
  useEffect(() => {
    if (!inView) return;
    let frame = 0;
    const total = 22;
    const id = setInterval(() => {
      frame++;
      const done = Math.floor((frame / total) * text.length);
      setOut(text.split("").map((c, i) => (i < done || c === " " ? c : GLYPHS[(Math.random() * GLYPHS.length) | 0])).join(""));
      if (frame >= total) { setOut(text); clearInterval(id); }
    }, 38);
    return () => clearInterval(id);
  }, [inView, text]);
  return <span ref={ref} className={className}>{out}</span>;
};

export const SectionHead = ({ index, eyebrow, title, children, className }) => (
  <div className={cn("grid gap-8 lg:grid-cols-12 lg:items-end", className)}>
    <div className="lg:col-span-7">
      <Reveal>
        <p className="eyebrow">
          <span className="text-slate-500">{index} —</span> <Scramble text={eyebrow} />
        </p>
      </Reveal>
      <h2 className="mt-5 font-display text-3xl font-bold leading-[1.02] tracking-tight text-white sm:text-4xl lg:text-5xl">
        <MaskLines lines={Array.isArray(title) ? title : [title]} onView />
      </h2>
    </div>
    {children && (
      <Reveal delay={0.15} className="text-base leading-relaxed text-slate-400 lg:col-span-4 lg:col-start-9">
        {children}
      </Reveal>
    )}
  </div>
);
